// Густота координатной сетки — настройка судоводителя.
//
// Капитаны просили сетку мельче: по ней снимают координату с экрана, и восемь
// делений на кадр для этого грубы. Настройка двигает ПОТОЛОК числа линий
// (GRATICULE_MAX_LINES), а не абсолютный шаг в градусах. Почему так:
// фиксированные, скажем, 10′ читаются на подходе к точке и превращаются в
// штриховку на обзоре — один и тот же выбор был бы то полезен, то бесполезен.
// Потолок же переживает зум сам собой: шаг остаётся круглым и подстраивается,
// а делений на экране всегда столько, сколько человек попросил.
//
// Хранится в localStorage этого браузера — как тема кабинета (theme.ts): своя
// на машине, без похода на сервер. Настройка живёт вне React-дерева, поэтому у
// неё своя подписка: карта и страница настроек — разные маршруты, и открытая
// карта должна узнать о смене сразу, а не после перезагрузки.

import { useSyncExternalStore } from 'react'
import { GRATICULE_MAX_LINES } from './graticule'

export type GraticuleDensity = 'sparse' | 'normal' | 'dense' | 'fine'

export interface GraticuleDensityLevel {
  id: GraticuleDensity
  /** Потолок числа линий на экран по каждой оси. */
  maxLines: number
}

/** Подпись уровня и подсказка «что человек увидит» — в каталоге перевода:
 *  `settings.map.graticule.<id>` и `settings.map.graticule.<id>.hint`. */
export function densityLabelKey(id: GraticuleDensity): string {
  return `settings.map.graticule.${id}`
}

export function densityHintKey(id: GraticuleDensity): string {
  return `settings.map.graticule.${id}.hint`
}

/** Уровни ПО ВОЗРАСТАНИЮ густоты. Шаг между соседними — примерно вдвое, то есть
 *  одно деление списка круглых шагов: разница видна глазом, лишних ступеней нет. */
export const GRATICULE_DENSITIES: GraticuleDensityLevel[] = [
  { id: 'sparse', maxLines: 4 },
  { id: 'normal', maxLines: GRATICULE_MAX_LINES },
  { id: 'dense', maxLines: 16 },
  { id: 'fine', maxLines: 32 },
]

// Умолчание — САМАЯ ЧАСТАЯ сетка (владелец, 18.08.2026): по делениям снимают
// координату с экрана, и просить об этом каждого судоводителя отдельно незачем.
// Умолчание касается только пустого хранилища — кто выбрал своё, сохранил своё.
const DEFAULT: GraticuleDensity = 'fine'
const KEY = 'cabinet.graticuleDensity'

/** Значение из хранилища → уровень. Терпимо к чужому: localStorage переживает
 *  выкладки, и там может лежать что угодно из прошлой версии. */
export function parseDensity(raw: string | null | undefined): GraticuleDensity {
  return GRATICULE_DENSITIES.some(d => d.id === raw) ? (raw as GraticuleDensity) : DEFAULT
}

/** Потолок линий для уровня. Незнакомый уровень получает умолчание — то же, что
 *  вернул бы `parseDensity`, иначе два входа в настройку разошлись бы. */
export function maxLinesFor(d: GraticuleDensity): number {
  const level = GRATICULE_DENSITIES.find(l => l.id === d)
  return (level ?? GRATICULE_DENSITIES.find(l => l.id === DEFAULT)!).maxLines
}

export function getGraticuleDensity(): GraticuleDensity {
  try {
    return parseDensity(localStorage.getItem(KEY))
  } catch {
    return DEFAULT
  }
}

const listeners = new Set<() => void>()

export function setGraticuleDensity(d: GraticuleDensity): void {
  try {
    localStorage.setItem(KEY, d)
  } catch {
    /* приватный режим — настройка просто не переживёт вкладку */
  }
  listeners.forEach(fn => fn())
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/** Густота сетки в React. Карта перерисовывается сразу после смены в настройках:
 *  подписка живёт вне дерева, поэтому переход между маршрутами ей не мешает. */
export function useGraticuleDensity(): GraticuleDensity {
  return useSyncExternalStore(subscribe, getGraticuleDensity, () => DEFAULT)
}
